import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { concatMap, map, catchError, of, withLatestFrom } from "rxjs";
import { SalesCreditRoleClient } from "src/api-service";
import { selectSelectedCompany } from "../../../../user/user.selectors";
import { SalesCreditRolesActions } from "./sales-credit-roles.actions";

@Injectable()
export class SalesCreditRolesEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly salesCreditRoleClient = inject(SalesCreditRoleClient);

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesCreditRolesActions.load),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([, selectedCompany]) =>
                this.salesCreditRoleClient.getAllAdmin(selectedCompany.id).pipe(
                    map(salesCreditRoles => SalesCreditRolesActions.loadSuccess({ salesCreditRoles: salesCreditRoles ?? [] })),
                    catchError(error => of(SalesCreditRolesActions.loadFailure({
                        error,
                        toastKey: "ADMIN_PANEL.EDIT_SALES_CREDIT_ROLE_SECTIONS.TOAST.LOAD_ERROR",
                    }))),
                ),
            ),
        ),
    );

    createSalesCreditRole$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesCreditRolesActions.createSalesCreditRole),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ request }, selectedCompany]) =>
                this.salesCreditRoleClient.create(selectedCompany.id, request).pipe(
                    map(response => SalesCreditRolesActions.createSalesCreditRoleSuccess({
                        response: response ?? [],
                        toastKey: "ADMIN_PANEL.EDIT_SALES_CREDIT_ROLE_SECTIONS.TOAST.CREATE_SUCCESS",
                    })),
                    catchError(error => of(SalesCreditRolesActions.createSalesCreditRoleFailure({
                        error,
                        toastKey: "ADMIN_PANEL.EDIT_SALES_CREDIT_ROLE_SECTIONS.TOAST.CREATE_ERROR",
                    }))),
                ),
            ),
        ),
    );

    updateSalesCreditRole$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesCreditRolesActions.updateSalesCreditRole),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ request, id }, selectedCompany]) =>
                this.salesCreditRoleClient.update(selectedCompany.id, id, request).pipe(
                    map(response => SalesCreditRolesActions.updateSalesCreditRoleSuccess({
                        response: response ?? [],
                        toastKey: "ADMIN_PANEL.EDIT_SALES_CREDIT_ROLE_SECTIONS.TOAST.UPDATE_SUCCESS",
                    })),
                    catchError(error => of(SalesCreditRolesActions.updateSalesCreditRoleFailure({
                        error,
                        toastKey: "ADMIN_PANEL.EDIT_SALES_CREDIT_ROLE_SECTIONS.TOAST.UPDATE_ERROR",
                    }))),
                ),
            ),
        ),
    );

    deactivateSalesCreditRole$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesCreditRolesActions.deactivateSalesCreditRole),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ id }, selectedCompany]) =>
                this.salesCreditRoleClient.deactivate(selectedCompany.id, id).pipe(
                    map(response => SalesCreditRolesActions.deactivateSalesCreditRoleSuccess({
                        response: response ?? [],
                        toastKey: "ADMIN_PANEL.EDIT_SALES_CREDIT_ROLE_SECTIONS.TOAST.DEACTIVATE_SUCCESS",
                    })),
                    catchError(error => of(SalesCreditRolesActions.deactivateSalesCreditRoleFailure({
                        error,
                        toastKey: "ADMIN_PANEL.EDIT_SALES_CREDIT_ROLE_SECTIONS.TOAST.DEACTIVATE_ERROR",
                    }))),
                ),
            ),
        ),
    );
}
